import React from 'react';
import {
    User, Bell, Calendar, FileText, LifeBuoy,
    ChevronRight, Clock, CheckCircle, AlertCircle,
    MessageSquare,
    // Link,
    ExternalLink
} from 'lucide-react';
import { useGetCustomerDashboardStats } from '../../custom-hooks/use-stats';
import { convertToPresentableText } from '../../utils/functions';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Progress, Spinner } from '@material-tailwind/react';
import { URLS } from '../../router-x/routes';

const CustomerDashboard = () => {

    const { data: stats, isLoading, error } = useGetCustomerDashboardStats();
    const [showAllBookings, setShowAllBookings] = React.useState(false);
    const [showAllNotifications, setShowAllNotifications] = React.useState(false);

    if (isLoading) {
        return <div className='flex justify-center items-center'>
            <Spinner />
        </div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    if (stats && stats.length === 0) {
        return <div>No stats found</div>;
    }

    return (
        <div className="min-h-screen bg-gray-100 px-4 py-6">
            <div className="container mx-auto max-w-6xl">
                <h1 className="text-2xl font-bold text-gray-800 mb-6">Welcome back, {convertToPresentableText(stats.customerId?.name || '')}</h1>

                {/* Quick Links */}
                <div className="grid grid-cols-1  md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <RouterLink to={URLS.CUSTOMER.PROFILE} className="bg-white p-4 rounded-full transition-shadow flex items-center justify-between">
                        <div className='flex items-center space-x-3'>
                            <div className="bg-blue-100 p-3 rounded-full">
                                <User className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className=''>
                                <h3 className="font-medium text-xs xl:text-base">Profile</h3>
                                <p className="text-xs text-gray-500 ">Manage your account</p>
                            </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400 ml-auto block md:hidden xl:block" />
                    </RouterLink>

                    <RouterLink to={URLS.CUSTOMER.MEETING_ROOM} className="bg-white p-4 rounded-full transition-shadow flex items-center justify-between">
                        <div className='flex items-center space-x-3'>
                            <div className="bg-green-100 p-3 rounded-full">
                                <Calendar className="h-6 w-6 text-green-600" />
                            </div>
                            <div className=''>
                                <h3 className="font-medium text-xs xl:text-base">Meeting Rooms</h3>
                                <p className="text-xs text-gray-500 ">Book a room</p>
                            </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400 ml-auto block md:hidden xl:block" />
                    </RouterLink>

                    <RouterLink to={URLS.CUSTOMER.CONTRACT} className="bg-white p-4 rounded-full transition-shadow flex items-center justify-between">
                        <div className='flex items-center space-x-3'>
                            <div className="bg-purple-100 p-3 rounded-full">
                                <FileText className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className=''>
                                <h3 className="font-medium text-xs xl:text-base">Contracts</h3>
                                <p className="text-xs text-gray-500 ">View your agreements</p>
                            </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400 ml-auto block md:hidden xl:block  " />
                    </RouterLink>

                    <RouterLink to="https://wa.me/923112200818" target='_blank' className="bg-white p-4 rounded-full transition-shadow flex items-center justify-between">
                        <div className='flex items-center space-x-3'>
                            <div className="bg-amber-100 p-3 rounded-full">
                                <LifeBuoy className="h-6 w-6 text-amber-600" />
                            </div>
                            <div className=''>
                                <h3 className="font-medium text-xs xl:text-base">Support</h3>
                                <p className="text-xs text-gray-500 ">Contact us</p>
                            </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400 ml-auto block md:hidden xl:block" />
                    </RouterLink>
                </div>

                {/* Meeting Quota Card */}
                <div className="bg-green-50 border border-green-200 rounded-2xl  p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="flex flex-col md:flex-row items-center space-x-4 mb-4 md:mb-0">
                            <div className="bg-gray-100 p-3 rounded-full">
                                <Calendar className="h-8 w-8 text-green-500" />
                            </div>
                            <div className='text-center md:text-left'>
                                <h3 className="text-lg font-medium text-gray-800">Meeting Room Quota</h3>
                                <p className="text-gray-600 text-xs md:text-base">{stats.meetingQuota?.remaining / 60} of {stats.meetingQuota?.total / 60} hours remaining this month</p>
                            </div>
                        </div>

                        <RouterLink
                            to={URLS.CUSTOMER.MEETING_ROOM}
                            className='hidden lg:block'
                        >
                            <Button size='md' color='green' className='rounded-full px-10'>
                                Book a Room
                            </Button>
                        </RouterLink>
                    </div>

                    <div className="mt-4">
                        <div className="w-full">
                            <Progress
                                value={parseInt(100 - ((stats.meetingQuota?.used / stats.meetingQuota?.total) * 100))}
                                color='green'
                                size='lg'
                                label={`Remaining`}
                            />
                        </div>
                        {/* <div className="w-full bg-green-100 rounded-full h-2.5">
                            <div
                                className="bg-green-600 h-2.5 rounded-full"
                                style={{ width: `${(stats.meetingQuota?.used / stats.meetingQuota?.total) * 100}%` }}
                            ></div>
                        </div> */}
                        <p className="text-xs text-gray-500 mt-2">
                            Quota resets on the 1st of each month
                            - Last reset: {stats.meetingQuota?.lastResetDate ? moment(stats.meetingQuota?.lastResetDate).format('DD MMM YYYY') : 'Never'}
                        </p>
                    </div>
                    <RouterLink
                        to={URLS.CUSTOMER.MEETING_ROOM}
                        className='block lg:hidden mt-4'
                    >
                        <Button size='md' color='green' className='rounded-full px-10 w-full'>
                            Book a Room
                        </Button>
                    </RouterLink>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Upcoming Bookings */}
                    <div className="bg-white rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">Upcoming Bookings</h2>
                            <RouterLink to={URLS.CUSTOMER.MEETING_ROOM} className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                                View all <ChevronRight className="h-4 w-4 ml-1" />
                            </RouterLink>
                        </div>
                        {stats.upcomingMeetingRoomBookings?.length > 0 ? (
                            <div className="space-y-4">
                                {stats.upcomingMeetingRoomBookings.slice(0, showAllBookings ? undefined : 3).map(booking => {
                                    return (
                                        <div key={booking._id} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50">
                                            <div className="flex items-start">
                                                <div className="bg-green-100 p-2 rounded-full mr-3">
                                                    <Clock className="h-5 w-5 text-green-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-medium text-gray-800">{booking.meetingRoom?.name}</h3>
                                                    <p className="text-sm text-gray-600">{booking.meetingRoom?.location}</p>
                                                    <div className="flex items-center mt-2 text-sm text-gray-500">
                                                        <span>{moment(booking.startDate).format('DD MMM YYYY')}</span>
                                                        <span className="mx-2">•</span>
                                                        <span>{moment(booking.startDate).format('h:mm A')} - {moment(booking.endDate).format('h:mm A')}</span>
                                                    </div>
                                                </div>
                                                <RouterLink
                                                    to={`${URLS.CUSTOMER.MEETING_ROOM}/booking/${booking._id}`}
                                                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
                                                >
                                                    Details
                                                </RouterLink>
                                            </div>
                                        </div>
                                    )
                                })}
                                {stats.upcomingMeetingRoomBookings.length > 3 && (
                                    <div className="text-center ">
                                        <Button
                                            variant="text"
                                            size="sm"
                                            className="text-blue-600 hover:text-blue-800"
                                            onClick={() => setShowAllBookings(!showAllBookings)}
                                        >
                                            {showAllBookings ? 'Show Less' : `Show ${stats.upcomingMeetingRoomBookings.length - 3} More`}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500">No upcoming bookings</p>
                                <RouterLink
                                    to={URLS.CUSTOMER.MEETING_ROOM}
                                    className="mt-2 inline-block text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    Book a meeting room
                                </RouterLink>
                            </div>
                        )}
                    </div>

                    {/* Notifications */}
                    <div className="bg-white rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center">
                                <h2 className="text-lg font-semibold text-gray-800">Notifications</h2>
                                {(stats?.notifications?.length > 0 && stats?.notifications?.filter(n => !n.read).length > 0) ? (
                                    <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                        {stats.notifications.filter(n => !n.read).length} new
                                    </span>
                                ) : (
                                    <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                        No new notifications
                                    </span>
                                )}
                            </div>
                            {/* <RouterLink
                                to="/c-notifications"
                                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                            >
                                View all <ChevronRight className="h-4 w-4 ml-1" />
                            </RouterLink> */}
                        </div>

                        {stats.notifications.length > 0 ? (
                            <div className="space-y-3">
                                {stats.notifications.slice(0, showAllNotifications ? undefined : 3).map(notification => (
                                    <div
                                        key={notification.id}
                                        className={`p-3 rounded-lg ${notification.read ? 'bg-gray-50' : 'bg-blue-50 border-l-4 border-blue-500'}`}
                                    >
                                        <div className="flex">
                                            <div className={`rounded-full p-2 mr-3 flex-shrink-0 ${notification.type === 'info' ? 'bg-blue-100' :
                                                notification.type === 'alert' ? 'bg-amber-100' : 'bg-green-100'
                                                }`}>
                                                {notification.type === 'info' ? (
                                                    <Bell className="h-4 w-4 text-blue-600" />
                                                ) : notification.type === 'alert' ? (
                                                    <AlertCircle className="h-4 w-4 text-amber-600" />
                                                ) : (
                                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-800">{notification.message}</p>
                                                <span className="text-xs text-gray-500">{notification.date}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {stats.notifications.length > 3 && (
                                    <div className="text-center pt-2">
                                        <Button
                                            variant="text"
                                            size="sm"
                                            className="text-blue-600 hover:text-blue-800"
                                            onClick={() => setShowAllNotifications(!showAllNotifications)}
                                        >
                                            {showAllNotifications ? 'Show Less' : `Show ${stats.notifications.length - 3} More`}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500">No notifications</p>
                            </div>
                        )}
                    </div>

                    {/* Active Contracts */}
                    <div className="bg-white rounded-lg  p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">Active Contracts</h2>
                            <RouterLink to={URLS.CUSTOMER.CONTRACT} className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                                View all <ChevronRight className="h-4 w-4 ml-1" />
                            </RouterLink>
                        </div>

                        {stats.contracts.length > 0 ? (
                            <div className="space-y-4">
                                {stats.contracts.map(contract => (
                                    <div key={contract.id} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50">
                                        <div className="flex items-start">
                                            <div className="bg-purple-100 p-2 rounded-full mr-3">
                                                <FileText className="h-5 w-5 text-purple-600" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center">
                                                    <h3 className="font-medium text-gray-800">{convertToPresentableText(contract.contractType)}</h3>
                                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${contract.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {contract.status}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600">{contract.location}</p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {moment(contract.startDate).format('MMM D, YYYY')} - {contract.endDate ? moment(contract.endDate).format('MMM D, YYYY') : `Notice ${contract.noticePeriod} weeks`}
                                                </p>
                                            </div>
                                            {/* <RouterLink //TODO: Add contract details page
                                                to={`/c-contract/${contract._id}`}
                                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
                                            >
                                                Details
                                            </RouterLink> */}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500">No active contracts</p>
                            </div>
                        )}
                    </div>

                    {/* Support Section */}
                    <div className="bg-white rounded-lg  p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Need Help?</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border border-gray-100 rounded-lg p-4 text-center">
                                <MessageSquare className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                                <h3 className="font-medium text-gray-800">Chat Support</h3>
                                <p className="text-sm text-gray-500 mb-3">Available 24/7 for you</p>
                                <RouterLink
                                    to="https://wa.me/923112200818" target='_blank'
                                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium w-full"
                                >
                                    Start Chat
                                </RouterLink>
                            </div>

                            <div className="border border-gray-100 rounded-lg p-4 text-center">
                                <LifeBuoy className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                                <h3 className="font-medium text-gray-800">Help Center</h3>
                                <p className="text-sm text-gray-500 mb-3">Contact our support team</p>
                                {/* <p className="text-sm text-gray-500 mb-3">Browse FAQs and tutorials</p> */}
                                <RouterLink
                                    to={URLS.CUSTOMER.SUPPORT}
                                    className="inline-block bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-full text-sm font-medium w-full"
                                >
                                    Visit Help Center
                                </RouterLink>
                            </div>
                        </div>

                        {/* <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 mr-3">
                                    <Link className="h-5 w-5 text-gray-500" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-gray-800">Quick Links</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                                        <RouterLink to="#" className="text-sm text-blue-600 hover:underline">FAQs</RouterLink>
                                        <RouterLink to="#" className="text-sm text-blue-600 hover:underline">Contact Us</RouterLink>
                                        <RouterLink to="#" className="text-sm text-blue-600 hover:underline">Billing</RouterLink>
                                        <RouterLink to="#" className="text-sm text-blue-600 hover:underline">Amenities</RouterLink>
                                    </div>
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerDashboard;